import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Settings2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { apiTokenService } from '@/services/apiTokenService'

const ROLE_LABELS = { admin: 'Admin', hr: 'HR', manager: 'Manager', employee: 'Employee' }

const Toggle = ({ id, label, description, checked, onChange, locked }) => (
  <label htmlFor={id} className="flex items-start justify-between gap-4 rounded-md border p-3">
    <span>
      <span className="block text-sm font-medium">{label}</span>
      <span className="block text-xs text-muted-foreground">{description}{locked && ' Turned off on the server (environment setting).'}</span>
    </span>
    <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={locked} aria-label={label} />
  </label>
)

// Admin: organisation-wide rules for AI tools. Changes apply to existing tokens and
// connections too (within about 30 seconds): narrowing a role narrows its tokens.
const AiAccessPolicyPanel = () => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery('ai-access-policy', apiTokenService.getPolicy)
  const info = data?.data
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (info?.policy) setDraft(JSON.parse(JSON.stringify(info.policy)))
  }, [info])

  if (isLoading || !draft) {
    return <Card><CardContent className="pt-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(info.policy)
  const set = (patch) => setDraft(d => ({ ...d, ...patch }))
  const toggleScope = (role, scope) => setDraft(d => {
    const has = d.roles[role].includes(scope)
    return { ...d, roles: { ...d.roles, [role]: has ? d.roles[role].filter(s => s !== scope) : [...d.roles[role], scope] } }
  })
  const setRole = (role, scopes) => setDraft(d => ({ ...d, roles: { ...d.roles, [role]: scopes } }))

  const save = async () => {
    setSaving(true)
    try {
      await apiTokenService.updatePolicy({
        mcp_enabled: draft.mcp_enabled,
        pat_enabled: draft.pat_enabled,
        oauth_enabled: draft.oauth_enabled,
        pat_max_days: Number(draft.pat_max_days),
        oauth_connection_days: Number(draft.oauth_connection_days),
        roles: draft.roles
      })
      toast.success('AI access policy saved')
      queryClient.invalidateQueries('ai-access-policy')
      queryClient.invalidateQueries('api-token-meta')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the policy')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><Settings2 className="h-5 w-5" /> AI access policy (admin)</CardTitle>
        <CardDescription>
          Rules for everyone. They apply to existing tokens and connected apps as well, within about 30 seconds.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-2 md:grid-cols-3">
          <Toggle id="policy-mcp" label="AI tools" description="The MCP server that Claude Code, Cursor and Claude connect to." checked={draft.mcp_enabled} onChange={(v) => set({ mcp_enabled: v })} locked={info.locked.mcp_enabled} />
          <Toggle id="policy-oauth" label="Connect by signing in" description="Apps connect through the browser and an Approve page." checked={draft.oauth_enabled} onChange={(v) => set({ oauth_enabled: v })} />
          <Toggle id="policy-pat" label="Personal access tokens" description="People can create tokens for scripts and tools." checked={draft.pat_enabled} onChange={(v) => set({ pat_enabled: v })} locked={info.locked.pat_enabled} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="policy-pat-days">Longest token lifetime (days)</Label>
            <Input id="policy-pat-days" type="number" min={1} max={info.max_pat_days} value={draft.pat_max_days} onChange={(e) => set({ pat_max_days: e.target.value })} className="w-32" />
            <p className="text-xs text-muted-foreground">For new tokens. Server maximum: {info.max_pat_days}.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="policy-oauth-days">Connected apps sign in again after (days)</Label>
            <Input id="policy-oauth-days" type="number" min={1} max={365} value={draft.oauth_connection_days} onChange={(e) => set({ oauth_connection_days: e.target.value })} className="w-32" />
            <p className="text-xs text-muted-foreground">For new connections.</p>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">What each role may let AI tools do</h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Permission</th>
                  {info.roles.map(role => <th key={role} className="px-3 py-2 text-center text-xs font-medium">{ROLE_LABELS[role] || role}</th>)}
                </tr>
              </thead>
              <tbody>
                {info.scopes.map(scope => (
                  <tr key={scope.id} className="border-b">
                    <td className="px-3 py-2">
                      <span className="font-medium">{scope.label}</span> <code className="text-[11px] text-muted-foreground">{scope.id}</code>
                    </td>
                    {info.roles.map(role => (
                      <td key={role} className="px-3 py-2 text-center">
                        <Checkbox
                          checked={draft.roles[role].includes(scope.id)}
                          onCheckedChange={() => toggleScope(role, scope.id)}
                          aria-label={`${ROLE_LABELS[role] || role}: ${scope.label}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-3 py-2 text-xs text-muted-foreground">Quick set</td>
                  {info.roles.map(role => (
                    <td key={role} className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-0.5 text-xs">
                        <button type="button" className="text-primary hover:underline" onClick={() => setRole(role, info.scopes.map(s => s.id))}>All</button>
                        <button type="button" className="text-primary hover:underline" onClick={() => setRole(role, info.scopes.filter(s => s.id.startsWith('read:')).map(s => s.id))}>Read only</button>
                        <button type="button" className="text-destructive hover:underline" onClick={() => setRole(role, [])}>None</button>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">A role with no permissions can't use AI tools at all. Tokens and apps never get more than their owner's role allows here.</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={!dirty || saving}>
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}Save policy
          </Button>
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(JSON.parse(JSON.stringify(info.policy)))}>Discard changes</Button>}
        </div>
      </CardContent>
    </Card>
  )
}

export default AiAccessPolicyPanel
